import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const STORAGE_KEY = 'acp_onboarding_seen';
const SKIP_PATHS = ['/login', '/signup', '/forgot-password', '/reset-password', '/admin-reset', '/auth/callback'];

const slides = [
  {
    title: 'Welcome to ACP',
    body: 'Every Apostolic conference in one place — UPCI, WPF, ALJC, AFF, and independent churches. Here\'s a quick tour.',
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    ),
  },
  {
    title: 'Find Your Conference',
    body: 'The Events page lists every upcoming conference. Filter by date, location, organization, or audience — men\'s, women\'s, youth, family.',
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    ),
  },
  {
    title: 'Every Detail in One Place',
    body: 'Each event page has the full schedule, speaker lineup, and venue details — plus one-tap links for flights, nearby hotels, and driving directions.',
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    ),
  },
  {
    title: 'Your Ticket, Always With You',
    body: 'Tap Register on any event and your QR ticket lands in My Tickets instantly. Show it at check-in — no printing needed.',
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
    ),
  },
  {
    title: 'Never Miss a Conference',
    body: 'Bookmark any event to save it to My Events. We\'ll send you reminders as the date approaches.',
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
    ),
  },
  {
    title: 'Bring Your Church Along',
    body: 'Every event has built-in Facebook sharing and a downloadable share card for Instagram stories — invite everyone in one tap.',
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m9.032 4.026a9.001 9.001 0 01-7.432 0m9.032-4.026A9.001 9.001 0 0112 3c-4.474 0-8.268 3.12-9.032 7.326m0 0A9.001 9.001 0 0012 21c4.474 0 8.268-3.12 9.032-7.326" />
    ),
  },
];

const OnboardingWalkthrough = () => {
  const { user, profile, loading } = useAuth();
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (loading || !user || !profile || profile.role !== 'attendee') return;
    if (SKIP_PATHS.some(p => window.location.pathname.startsWith(p))) return;
    if (localStorage.getItem(STORAGE_KEY)) return;

    const timer = setTimeout(() => setVisible(true), 800);
    return () => clearTimeout(timer);
  }, [loading, user, profile]);

  if (!visible) return null;

  const isLast = step === slides.length - 1;

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, '1');
    setVisible(false);
  };

  const finish = () => {
    dismiss();
    navigate('/events');
  };

  const slide = slides[step];

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 px-4" role="dialog" aria-modal="true" aria-label="Getting started guide">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-br from-primary-dark via-primary to-primary-light px-6 py-5 relative">
          <p className="text-xs font-semibold tracking-widest text-purple-200 uppercase">Getting Started</p>
          <p className="text-white text-sm mt-1">Step {step + 1} of {slides.length}</p>
          <button
            onClick={dismiss}
            className="absolute top-4 right-4 text-purple-200 hover:text-white transition-colors"
            aria-label="Skip guide"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Slide content */}
        <div className="px-6 py-8 text-center">
          <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-primary/10 text-primary flex items-center justify-center">
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {slide.icon}
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-3">{slide.title}</h2>
          <p className="text-sm text-gray-600 leading-relaxed">{slide.body}</p>
        </div>

        {/* Dots */}
        <div className="flex justify-center gap-2 pb-4">
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => setStep(i)}
              aria-label={`Go to step ${i + 1}`}
              className={`h-2 rounded-full transition-all ${i === step ? 'w-6 bg-primary' : 'w-2 bg-gray-300'}`}
            />
          ))}
        </div>

        {/* Footer buttons */}
        <div className="px-6 pb-6 flex gap-3">
          {!isLast && (
            <button
              onClick={dismiss}
              className="px-5 py-3 text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors"
            >
              Skip
            </button>
          )}
          <button
            onClick={isLast ? finish : () => setStep(step + 1)}
            className="flex-1 px-6 py-3 bg-primary hover:bg-primary-dark text-white text-sm font-semibold rounded-xl transition-colors"
          >
            {isLast ? 'Start Exploring Events' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OnboardingWalkthrough;
